import { useQuery } from '@tanstack/react-query'
import React from 'react'
import * as R from 'remeda'

import { type UseSurveyQuery_MainQuery as Data } from '~/gql/graphql'
import { exists, filterExists } from '~/utils'

import { useSurveyQueryKey } from './useSurveyQueryKey'

export const useSurveyQueryData = () => {
  const queryKey = useSurveyQueryKey()
  const { data } = useQuery<Data>({ enabled: false, queryKey })

  return React.useMemo(() => {
    const survey = data?.survey

    if (!exists(survey)) {
      return null
    }

    return R.pipe(survey, (survey) => ({
      ...survey,
      brandGroups: R.pipe(
        survey.brandGroups,
        filterExists,
        R.map((brandGroup) => ({
          ...brandGroup,
          brands: R.pipe(brandGroup.brands, filterExists),
        })),
      ),
      questions: R.pipe(survey.questions, filterExists),
      customerAttributeGroups: R.pipe(
        survey.customerAttributeGroups ?? [],
        R.map((customerAttributeGroup) => ({
          ...customerAttributeGroup,
          customerAttributes: R.pipe(
            customerAttributeGroup.customerAttributes ?? [],
            R.map((customerAttribute) => ({
              ...customerAttribute,
              filterGroups: R.pipe(
                customerAttribute.filterGroups ?? [],
                R.map((filterGroup) =>
                  filterGroup.map((filter) => ({
                    ...filter,
                    question: exists(filter.question)
                      ? {
                          ...filter.question,
                          answerOptions: filter.question.answerOptions ?? [],
                        }
                      : null,
                    customerAttribute: filter.customerAttribute ?? null,
                    brandSegments: filter.brandSegments ?? null,
                  })),
                ),
              ),
            })),
          ),
        })),
      ),
    }))
  }, [data?.survey])
}

export type Survey = NonNullable<ReturnType<typeof useSurveyQueryData>>
export type BrandGroup = Survey['brandGroups'][number]
export type Brand = BrandGroup['brands'][number]
export type CustomerAttributeGroup = Survey['customerAttributeGroups'][number]
export type CustomerAttribute =
  CustomerAttributeGroup['customerAttributes'][number]
export type CustomerAttributeFilter =
  CustomerAttribute['filterGroups'][number][number]
export type Question = Survey['questions'][number]
export type AnswerOption = Question['answer_options'][number]
